<!-- Quick Pay Header - Only show in embedded mode when not in property selection -->
<div
  class="quick-pay-expanded-header"
  *ngIf="isEmbedded && !showPropertySelection"
  role="banner"
>
  <div class="quick-pay-icon">
    <img
      src="assets/images/quick_pay_logo.svg"
      alt="Quick Pay"
      aria-hidden="true"
    />
  </div>
  <h3 class="quick-pay-title" id="quick-pay-heading">Quick Pay</h3>

  <!-- Description for credentials/account views -->
  <p class="quick-pay-description-expanded">
    Want to pay your bill online? Simply enter your 16-digit account number or
    enter your credentials, continue with Google.
  </p>
  <div class="quick-pay-info-link">
    <button
      type="button"
      (click)="showAccountInfo($event)"
      aria-label="Where to locate my account information"
      title="Information"
    >
      <app-icon
        name="information-circle-outline"
        size="20px"
        color="#121F38"
        aria-hidden="true"
      />
    </button>
    <span id="account-info-label">Where to locate my account information?</span>
  </div>
</div>

<!-- Property Selection Container - Show for ALL modes (embedded and non-embedded) -->
<div
  class="property-selection-wrapper"
  *ngIf="showPropertySelection"
  role="region"
  aria-labelledby="property-selection-heading"
>
  <!-- Header for embedded mode during property selection -->
  <div class="quick-pay-expanded-header" *ngIf="isEmbedded" role="banner">
    <div class="quick-pay-icon">
      <img
        src="assets/images/quick_pay_logo.svg"
        alt="Quick Pay"
        aria-hidden="true"
      />
    </div>
    <h3 class="quick-pay-title" id="property-selection-heading">Quick Pay</h3>
  </div>

  <!-- Single Property Found -->
  <div
    *ngIf="hasProperties && quickPaymentProperties.length === 1"
    class="property-selection-container"
    role="region"
    aria-label="Property confirmation"
  >
    <p class="property-selection-message" role="status">
      We found your account!
    </p>
    <p class="property-selection-submessage">
      Please confirm your property address before proceeding to payment.
    </p>
    <div class="property-info-block" aria-label="Selected property details">
      <div class="property-check-icon" aria-hidden="true">
        <i class="dx-icon dx-icon-check"></i>
      </div>
      <div class="property-details">
        <div class="property-community-name">
          <strong>{{ quickPaymentProperties[0].communityName }}</strong>
        </div>
        <div class="property-address">
          {{ quickPaymentProperties[0].propertyAddress }}
        </div>
        <div class="property-account-number">
          {{ quickPaymentProperties[0].customerId }}
        </div>
      </div>
    </div>
    <div class="property-action-buttons" role="group" aria-label="Actions">
      <dx-button
        text="Back"
        type="default"
        class="back-button"
        [width]="'100%'"
        (onClick)="goBack()"
        aria-label="Go back to login"
      ></dx-button>
      <dx-button
        text="Confirm"
        type="success"
        class="confirm-button"
        [width]="'100%'"
        (onClick)="confirmSingleProperty()"
        aria-label="Confirm property and proceed to payment"
      ></dx-button>
    </div>
  </div>

  <!-- Multiple Properties Found -->
  <div
    *ngIf="hasProperties && quickPaymentProperties.length > 1"
    class="property-selection-container"
    role="region"
    aria-label="Property selection"
  >
    <p class="property-selection-message">
      We found multiple accounts associated with your account number.
    </p>
    <p class="property-selection-submessage" id="property-list-description">
      Please select a property before proceeding to payment.
    </p>
    <div
      class="property-list"
      role="radiogroup"
      aria-labelledby="property-list-description"
    >
      <div
        *ngFor="
          let property of quickPaymentProperties;
          trackBy: trackByPropertyId
        "
        class="property-option"
        [class.selected]="selectedCustomerId === property.customerId"
        (click)="selectProperty(property.customerId)"
        (keydown.enter)="selectProperty(property.customerId)"
        (keydown.space)="
          selectProperty(property.customerId); $event.preventDefault()
        "
        [attr.tabindex]="0"
        role="radio"
        [attr.aria-checked]="selectedCustomerId === property.customerId"
        [attr.aria-label]="
          property.communityName + ', ' + property.propertyAddress
        "
      >
        <div class="property-radio-wrapper">
          <div
            *ngIf="selectedCustomerId === property.customerId"
            class="property-check-icon"
            aria-hidden="true"
          >
            <i class="dx-icon dx-icon-check"></i>
          </div>
          <span
            *ngIf="selectedCustomerId !== property.customerId"
            class="property-radio-circle"
            aria-hidden="true"
          ></span>
        </div>
        <div class="property-option-details">
          <div class="property-community-name">
            <strong>{{ property.communityName }}</strong>
          </div>
          <div class="property-address">{{ property.propertyAddress }}</div>
          <div class="property-account-number">{{ property.customerId }}</div>
        </div>
      </div>
    </div>
    <div class="property-action-buttons" role="group" aria-label="Actions">
      <dx-button
        text="Back"
        type="default"
        class="back-button"
        [width]="'100%'"
        (onClick)="goBack()"
        aria-label="Go back to login"
      ></dx-button>
      <dx-button
        text="Confirm"
        type="default"
        class="confirm-button"
        [width]="'100%'"
        (onClick)="onConfirm()"
        [disabled]="!selectedCustomerId"
        aria-label="Confirm selected property and proceed to payment"
      ></dx-button>
    </div>
  </div>

  <!-- No Properties Found -->
  <div
    *ngIf="!hasProperties"
    class="property-selection-container"
    role="alert"
    aria-live="polite"
  >
    <p class="property-selection-message error-message">
      {{ noPropertyMessage }}
    </p>
    <div class="property-action-buttons">
      <dx-button
        text="Back"
        type="default"
        class="back-button"
        [width]="'100%'"
        (onClick)="goBack()"
        aria-label="Go back to login"
      ></dx-button>
    </div>
  </div>
</div>

<!-- Payment Form Container - Only show when not in property selection mode -->
<div
  class="payment-form-container"
  *ngIf="!showPropertySelection"
  role="main"
  aria-labelledby="quick-pay-heading"
>
  <!-- Error Message -->
  <div
    *ngIf="errorMessage"
    class="quick-payment-error-container"
    role="alert"
    aria-live="assertive"
  >
    <div class="quick-payment-error-message">
      {{ errorMessage }}
    </div>
  </div>

  <!-- CREDENTIALS VIEW (Default) -->
  <div
    *ngIf="currentViewMode === 'credentials'"
    class="quick-payment-form"
    role="form"
    aria-label="Login with credentials"
  >
    <dx-validation-group #validatorLogin>
      <!-- Email Field -->
      <div class="quick-payment-input-container">
        <div class="quick-payment-input-wrapper">
          <label for="txtUsername" class="input-label" id="email-label">
            Email
            <span class="required-asterisk" aria-hidden="true">*</span>
            <span class="visually-hidden">required</span>
          </label>
          <dx-text-box
            ccTrimInput
            id="txtUsername"
            placeholder="Enter Email"
            [(value)]="loginParam.username"
            class="text-input"
            (onEnterKey)="proceedToPayment()"
            aria-labelledby="email-label"
            aria-required="true"
          >
            <dx-validator>
              <dxi-validation-rule
                type="required"
                message="Email is required"
              ></dxi-validation-rule>
              <dxi-validation-rule
                type="pattern"
                [pattern]="emailPattern"
                message="Invalid email format"
              ></dxi-validation-rule>
            </dx-validator>
          </dx-text-box>
        </div>
      </div>

      <!-- Password Field -->
      <div class="quick-payment-input-container">
        <div class="quick-payment-input-wrapper">
          <label for="txtPassword" class="input-label" id="password-label">
            Password
            <span class="required-asterisk" aria-hidden="true">*</span>
            <span class="visually-hidden">required</span>
          </label>
          <dx-text-box
            ccTrimInput
            id="txtPassword"
            placeholder="Enter Password"
            [(mode)]="passwordMode"
            [(value)]="loginParam.password"
            (onEnterKey)="proceedToPayment()"
            class="text-input"
            aria-labelledby="password-label"
            aria-required="true"
          >
            <dxi-button
              name="password"
              location="after"
              [options]="{
                icon: passwordButtonIcon,
                type: 'default',
                stylingMode: 'text',
                hint: passwordButtonHint,
                onClick: passwordButtonOptions.onClick
              }"
            ></dxi-button>
            <dx-validator name="passwordValidator">
              <dxi-validation-rule
                type="required"
                message="Password is required"
              ></dxi-validation-rule>
            </dx-validator>
          </dx-text-box>
        </div>
      </div>
    </dx-validation-group>

    <!-- Continue to Payment Button -->
    <div class="quick-payment-button-container">
      <dx-button
        text="Continue to Payment"
        type="default"
        class="continue-payment-btn"
        [width]="'100%'"
        [height]="40"
        [disabled]="loadingVisible"
        (onClick)="proceedToPayment()"
        aria-label="Continue to payment with credentials"
      ></dx-button>
    </div>

    <!-- OR Divider -->
    <div class="quickpay-divider" *ngIf="showGoogleAuth" aria-hidden="true">
      <span><span></span></span>
      <div>OR</div>
      <span><span></span></span>
    </div>

    <!-- Google Sign-In Button -->
    <div class="google-signin-container" *ngIf="showGoogleAuth">
      <dx-button
        class="google-signin-button"
        type="default"
        [disabled]="loadingVisible"
        (onClick)="onGoogleSignInClick()"
        aria-label="Continue with Google account"
      >
        <div class="google-button-content">
          <app-icon
            name="google-logo"
            size="16px"
            class="google-logo"
            aria-hidden="true"
          ></app-icon>
          <span>Continue with Google</span>
        </div>
      </dx-button>
    </div>

    <!-- Toggle to Account Number -->
    <div class="view-toggle-container">
      <span>Prefer to use your 16-digit account number instead?</span>
      <a
        href="#"
        (click)="switchToAccountNumber(); $event.preventDefault()"
        (keydown.enter)="switchToAccountNumber(); $event.preventDefault()"
        role="button"
        aria-label="Switch to account number login"
      >
        Use my account number
      </a>
    </div>
  </div>

  <!-- ACCOUNT NUMBER VIEW -->
  <div
    *ngIf="currentViewMode === 'account-number'"
    class="quick-payment-form"
    role="form"
    aria-label="Login with account number"
  >
    <dx-validation-group #validateAccountNumber width="100%">
      <div class="quick-payment-input-container">
        <div class="quick-payment-input-wrapper">
          <label for="txtAccountNumber" class="input-label" id="account-label">
            Account Number
            <span class="required-asterisk" aria-hidden="true">*</span>
            <span class="visually-hidden">required</span>
          </label>
          <dx-text-box
            id="txtAccountNumber"
            placeholder="Enter Account Number"
            class="text-input"
            [(value)]="accountNumber"
            (onEnterKey)="proceedToPayment()"
            aria-labelledby="account-label"
            aria-required="true"
            aria-describedby="account-hint"
          >
            <dx-validator>
              <dxi-validation-rule
                type="required"
                message="Account number is required"
              ></dxi-validation-rule>
              <dxi-validation-rule
                type="pattern"
                [pattern]="accountNumberPattern"
                message="Invalid Account number"
              ></dxi-validation-rule>
            </dx-validator>
          </dx-text-box>
          <span id="account-hint" class="visually-hidden">
            Enter your 16-digit account number found on your statement
          </span>
        </div>
      </div>
    </dx-validation-group>

    <!-- Continue to Payment Button -->
    <div class="quick-payment-button-container">
      <dx-button
        text="Continue to Payment"
        type="default"
        class="continue-payment-btn"
        [width]="'100%'"
        [height]="40"
        [disabled]="loadingVisible"
        (onClick)="proceedToPayment()"
        aria-label="Continue to payment with account number"
      ></dx-button>
    </div>

    <!-- Toggle to Credentials -->
    <div class="view-toggle-container">
      <span>Prefer to use your credentials instead?</span>
      <a
        href="#"
        (click)="switchToCredentials(); $event.preventDefault()"
        (keydown.enter)="switchToCredentials(); $event.preventDefault()"
        role="button"
        aria-label="Switch to credentials login"
      >
        Use my credentials
      </a>
    </div>
  </div>
</div>

<!-- Account Info Popup -->
<dx-popup
  width="75%"
  height="auto"
  [showTitle]="true"
  [showCloseButton]="true"
  title="Where to locate my account information?"
  [dragEnabled]="true"
  [hideOnOutsideClick]="true"
  [(visible)]="isAccountInfoShow"
  role="dialog"
  aria-labelledby="account-info-dialog-title"
>
  <div *dxTemplate="let data of 'content'">
    <div class="container">
      <div class="row help-text-container">
        Your Account Number can be found on your statement as highlighted below.
      </div>
      <div class="row">
        <img
          [src]="helpImage"
          class="help-pop-up-image"
          alt="Statement showing account number location highlighted"
        />
      </div>
    </div>
  </div>
</dx-popup>

<dx-load-panel
  shadingColor="rgba(0,0,0,0.4)"
  [position]="{ of: 'window' }"
  [(visible)]="loadingVisible"
  [showIndicator]="true"
  [showPane]="true"
  [shading]="true"
  [hideOnOutsideClick]="false"
  aria-label="Loading"
  role="status"
></dx-load-panel>
